import Provider from "./new/provider";
import {Constructor} from "../../global/constructor";
import {IResponse, IServerData} from "~/interfaces/serverInterfaces"
import {HTTPMethod} from "./new/httpMethod";
import {RouteHelper} from "~/helpers/routeHelper";
import {Route} from "~/enums/route";
import {IProviderRoutes} from "~/interfaces/provider";
import ProcessFlow from "~/models/processFlows/processFlow";
import {Hash} from "~/interfaces/hashInterfaces";

interface IProcessFlowProviderRoutes {
  create: string;
  fetchCertificateDynamicInputs: string;
  searchForms: Route;
  searchObjectModels: Route;
  searchProcessFlows: Route;
  searchDetailViews: Route;
  searchListViews: Route;
  searchCertificates: Route;
  getObjectModelRelations: Route;
  publishingDependencies: Route;
  publish: Route;
}

export default class ProcessFlowProvider<T extends ProcessFlow = ProcessFlow> extends Provider<T> {
  routes: IProviderRoutes & IProcessFlowProviderRoutes;

  constructor(
    protected modelType: Constructor<T>,
    public modelRoot: string,
    public collectionRoot: string
  ) {
    super(modelType, modelRoot, collectionRoot);
    let newRoutes: IProcessFlowProviderRoutes = {
      create: RouteHelper.path({ route: Route.processFlows }),
      fetchCertificateDynamicInputs: RouteHelper.path({route: Route.fetchCertificateDynamicInputs}),
      searchForms: Route.processFlowSearchForms,
      searchProcessFlows: Route.processFlowSearchProcessFlows,
      searchObjectModels: Route.processFlowSearchObjectModels,
      searchDetailViews: Route.processFlowSearchDetailViews,
      searchListViews: Route.processFlowSearchListViews,
      searchCertificates: Route.processSearchCertificates,
      getObjectModelRelations: Route.getObjectModelRelations,
      publishingDependencies: Route.processFlowPublishingDependencies,
      publish: Route.publishProcessFlow,
    };
    Object.assign(this.routes, newRoutes);
  }

  create({organizationId, folderIdTree, processFlow}: {
    organizationId: string
    folderIdTree: string
    processFlow: object
  }): Promise<IServerData> {
    const json = {
      organization_id: organizationId,
      folder_id_tree: folderIdTree,
      process_flow: processFlow,
    }
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: this.routes.create,
        method: HTTPMethod.POST,
        json: json,
      }).then(resolve)
    })
  }

  fetchCertificateDynamicInputs({certificateId}: {
    certificateId: string
  }): Promise<IServerData> {
    const json = {
      certificate_id: certificateId,
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: this.routes.fetchCertificateDynamicInputs,
        json: json,
      }).then(resolve);
    });
  }

  searchForms(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchForms, modelOrId: obj}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  searchObjectModels(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchObjectModels, modelOrId: obj}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  searchProcessFlows(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchProcessFlows}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  searchDetailViews(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchDetailViews, modelOrId: obj}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  searchListViews(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchListViews, modelOrId: obj}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  searchCertificates(obj: T, json?: object): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.searchCertificates, modelOrId: obj}),
      method: HTTPMethod.GET,
      json: json,
    })
  }

  fetchObjectModelRelations({objectModelId}: {
    objectModelId: string
  }): Promise<IServerData<Hash>> {
    const json = {
      object_model_id: objectModelId,
    };
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.getObjectModelRelations}),
      json: json,
    });
  }

  publish(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.publish, modelOrId: obj}),
        method: HTTPMethod.POST,
      }).then(resolve)
    })
  }

  publishingDependencies(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.publishingDependencies, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    })
  }
}